/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { InfoPopover } from "components/InfoPopover"
import InputError from "components/InputError"
import { CurrentUserParameters } from "features/userParameters/UserParametersSchemata"
import { FC } from "react"
import { useFormContext } from "react-hook-form"

export enum AssetsType {
  Initial = "initial",
  Final = "final",
}

export interface AssetsProps {
  readonly legend: string
  readonly assetsType: AssetsType
}

const Assets: FC<Readonly<AssetsProps>> = ({ assetsType }) => {
  const {
    register,
    trigger,
    formState: { errors },
  } = useFormContext<CurrentUserParameters>()

  const assetsStr = assetsType === AssetsType.Initial ? "initialAssets" : "finalAssets"
  const assetErrors = errors[assetsStr]

  // Helper function to determine if there are any field-specific allocation errors
  const hasFieldSpecificAllocationErrors = () => {
    if (!assetErrors?.allocation) return false

    // Check if any individual allocation fields have errors
    return !!(
      assetErrors.allocation.spxTrPct ??
      assetErrors.allocation.worldExUsEquitiesPct ??
      assetErrors.allocation.cashPct ??
      assetErrors.allocation.bm10yPct ??
      assetErrors.allocation.bm30yPct ??
      assetErrors.allocation.commoditiesPct ??
      assetErrors.allocation.goldPct
    )
  }

  // Should highlight all fields when there's a collective error (sum ≠ 100%) but no field-specific errors
  const highlightAllFields = assetErrors?.allocation && !hasFieldSpecificAllocationErrors()

  return (
    <>
      <fieldset className="form-group mb-4" id={`${assetsType}-assets-fieldset`}>
        <legend className={`mb-3 ${assetErrors?.allocation ? "is-invalid" : ""}`}>
          Allocation
          <InfoPopover
            htmlContent={true}
            content="<p>Set your asset allocation as percentages that total 100%. Note that the Fama-French factor loadings don't contribute to this sum.</p>
            <p>See our <a href='/methodology/#data-sources' target='_blank'>methodology page</a> for details on how returns for each asset class are constructed.</p>
            "
          />
        </legend>
        <InputError error={assetErrors?.allocation} />
        <div className="row">
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-expense-ratio-per-annum-pct`} className="col-form-label">
              Expense Ratio
              <InfoPopover content="The weighted average proportional per-annum expense ratio of your investment portfolio. Include ETF and mutual fund expense ratios here. Also, add AUM investment advisor fee if applicable." />
            </label>
            <div className="input-group">
              <input
                type="string"
                inputMode="decimal"
                className={`form-control expense-ratio-input${assetErrors?.expenseRatioPerAnnumPct ? " is-invalid" : ""}`}
                id={`${assetsType}-expense-ratio-per-annum-pct`}
                aria-invalid={!!assetErrors?.expenseRatioPerAnnumPct}
                {...register(`${assetsStr}.expenseRatioPerAnnumPct`)}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.expenseRatioPerAnnumPct} />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-spx-tr-pct`} className="col-form-label">
              Stocks: S&P 500
            </label>
            <div className="input-group">
              <input
                type="string"
                inputMode="decimal"
                className={`form-control${assetErrors?.allocation?.spxTrPct || highlightAllFields ? " is-invalid" : ""}`}
                id={`${assetsType}-spx-tr-pct`}
                aria-invalid={!!(assetErrors?.allocation?.spxTrPct ?? highlightAllFields)}
                {...register(`${assetsStr}.allocation.spxTrPct`, {
                  onBlur: () => {
                    void trigger(`${assetsStr}.allocation`)
                  },
                })}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.allocation?.spxTrPct} />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-world-ex-us-equities-pct`} className="col-form-label">
              Stocks: Non-US
              <InfoPopover
                htmlContent={true}
                content="1970 - current. See the <a href='/methodology/'>methodology</a> page."
              />
            </label>
            <div className="input-group">
              <input
                type="string"
                className={`form-control${assetErrors?.allocation?.worldExUsEquitiesPct || highlightAllFields ? " is-invalid" : ""}`}
                id={`${assetsType}-world-ex-us-equities-pct`}
                aria-invalid={
                  !!(assetErrors?.allocation?.worldExUsEquitiesPct ?? highlightAllFields)
                }
                {...register(`${assetsStr}.allocation.worldExUsEquitiesPct`, {
                  onBlur: () => {
                    void trigger(`${assetsStr}.allocation`)
                  },
                })}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.allocation?.worldExUsEquitiesPct} />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-cash-pct`} className="col-form-label">
              Cash: 3m U.S. T-bills
            </label>
            <div className="input-group">
              <input
                type="string"
                inputMode="decimal"
                className={`form-control${assetErrors?.allocation?.cashPct || highlightAllFields ? " is-invalid" : ""}`}
                id={`${assetsType}-cash-pct`}
                aria-invalid={!!(assetErrors?.allocation?.cashPct ?? highlightAllFields)}
                {...register(`${assetsStr}.allocation.cashPct`, {
                  onBlur: () => {
                    void trigger(`${assetsStr}.allocation`)
                  },
                })}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.allocation?.cashPct} />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-bm-10y-pct`} className="col-form-label">
              Bonds: 10y U.S. Treasury
            </label>
            <div className="input-group">
              <input
                type="string"
                inputMode="decimal"
                className={`form-control${assetErrors?.allocation?.bm10yPct || highlightAllFields ? " is-invalid" : ""}`}
                id={`${assetsType}-bm-10y-pct`}
                aria-invalid={!!(assetErrors?.allocation?.bm10yPct ?? highlightAllFields)}
                {...register(`${assetsStr}.allocation.bm10yPct`, {
                  onBlur: () => {
                    void trigger(`${assetsStr}.allocation`)
                  },
                })}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.allocation?.bm10yPct} />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-bm-30y-pct`} className="col-form-label">
              Bonds: 30y U.S. Treasury
            </label>
            <div className="input-group">
              <input
                type="string"
                inputMode="decimal"
                className={`form-control${assetErrors?.allocation?.bm30yPct || highlightAllFields ? " is-invalid" : ""}`}
                id={`${assetsType}-bm-30y-pct`}
                aria-invalid={!!(assetErrors?.allocation?.bm30yPct ?? highlightAllFields)}
                {...register(`${assetsStr}.allocation.bm30yPct`, {
                  onBlur: () => {
                    void trigger(`${assetsStr}.allocation`)
                  },
                })}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.allocation?.bm30yPct} />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-commodities-pct`} className="col-sm-3 col-form-label">
              Commodities
            </label>
            <div className="input-group">
              <input
                type="string"
                className={`form-control${assetErrors?.allocation?.commoditiesPct || highlightAllFields ? " is-invalid" : ""}`}
                id={`${assetsType}-commodities-pct`}
                aria-invalid={!!(assetErrors?.allocation?.commoditiesPct ?? highlightAllFields)}
                {...register(`${assetsStr}.allocation.commoditiesPct`, {
                  onBlur: () => {
                    void trigger(`${assetsStr}.allocation`)
                  },
                })}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.allocation?.commoditiesPct} />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-gold-pct`} className="col-sm-3 col-form-label">
              Gold
            </label>
            <div className="input-group">
              <input
                type="string"
                className={`form-control${assetErrors?.allocation?.goldPct || highlightAllFields ? " is-invalid" : ""}`}
                id={`${assetsType}-gold-pct`}
                aria-invalid={!!(assetErrors?.allocation?.goldPct ?? highlightAllFields)}
                {...register(`${assetsStr}.allocation.goldPct`, {
                  onBlur: () => {
                    void trigger(`${assetsStr}.allocation`)
                  },
                })}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.allocation?.goldPct} />
            </div>
          </div>
        </div>
      </fieldset>
      <fieldset className="form-group my-3" id={`${assetsType}-fama-french-factors`}>
        <legend className="mb-3">
          Fama-French Factors
          <InfoPopover
            content="Only taken into account after July 1926. See the <a href='/guide/'>Guide</a> for more information."
            htmlContent={true}
          />
        </legend>
        <div className="row">
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-small-stocks-pct`} className="col-form-label">
              Small Stocks (SMB)
            </label>
            <div className="input-group">
              <input
                type="string"
                className={`form-control${assetErrors?.famaFrenchFactors?.smallStocksPct ? " is-invalid" : ""}`}
                id={`${assetsType}-small-stocks-pct`}
                {...register(`${assetsStr}.famaFrenchFactors.smallStocksPct`)}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.famaFrenchFactors?.smallStocksPct} />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`${assetsType}-value-stocks-pct`} className="col-form-label">
              Value Stocks (HML)
            </label>
            <div className="input-group">
              <input
                type="string"
                className={`form-control${assetErrors?.famaFrenchFactors?.valueStocksPct ? " is-invalid" : ""}`}
                id={`${assetsType}-value-stocks-pct`}
                {...register(`${assetsStr}.famaFrenchFactors.valueStocksPct`)}
              />
              <span className="input-group-text">%</span>
              <InputError error={assetErrors?.famaFrenchFactors?.valueStocksPct} />
            </div>
          </div>
        </div>
      </fieldset>
    </>
  )
}

export default Assets
